/**
 * Generates a hash code from a string.
 * Similar to Java's String.hashCode().
 *
 * @param {string} str - The input string.
 * @returns {number} The hash code.
 */
export function hashCode(str: string): number {
  if (!str) return 0;
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

/**
 * Converts HSL color values to RGB.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param {number} h - The hue (0 to 1).
 * @param {number} s - The saturation (0 to 1).
 * @param {number} l - The lightness (0 to 1).
 * @returns {[number, number, number]} The RGB representation [r, g, b].
 */
export function hslToRgb(
  h: number,
  s: number,
  l: number
): [number, number, number] {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Generates a visually distinct hex color string based on an input number (e.g., a hash code).
 * Aims for good saturation and lightness, avoiding pure white/black/grays.
 *
 * @param {number} i - The input number (e.g., hash code).
 * @returns {string} A hex color string (e.g., "#AABBCC").
 */
export function generateColor(i: number): string {
  // Use the input number to determine the Hue (0-359 degrees)
  const h = (i % 360) / 360; // Scale to 0-1 range for HSL

  // Set saturation and lightness with some variance based on the input
  const s = 0.5 + ((i >> 8) % 50) / 100; // Saturation ~0.5 to 1.0
  const l = 0.55 + ((i >> 16) % 10) / 100; // Lightness ~0.55 to 0.65

  // Convert HSL to RGB, ensuring s and l are clamped [0, 1]
  const [r, g, b] = hslToRgb(h, Math.min(1, s), Math.min(1, l));

  // Convert RGB to Hex
  const finalHex = `#${((1 << 24) + (r << 16) + (g << 8) + b)
    .toString(16)
    .slice(1)
    .padStart(6, "0") // Ensure 6 digits
    .toUpperCase()}`;

  return finalHex;
}
